/**
 * The maths the canvas needs, with no canvas in it.
 *
 * Everything here is a pure function of numbers, which is what makes hit
 * testing and stroke simplification checkable — the two places a drawing tool
 * is wrong in a way no screenshot shows.
 *
 * ── World units, and one transform ──────────────────────────────────────────
 * Elements are stored in world units on an infinite plane. A `View` is the
 * window onto it: `x, y` is the world point at the top-left of the viewport and
 * `scale` is pixels per world unit. Every pointer position is turned into world
 * units on the way in and nothing downstream knows the viewport exists.
 */

import type { BoardElement } from '../../lib/board/types';

export interface View {
  x: number;
  y: number;
  scale: number;
}

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export const MIN_SCALE = 0.1;
export const MAX_SCALE = 8;

export const toWorld = (view: View, sx: number, sy: number): { x: number; y: number } => ({
  x: view.x + sx / view.scale,
  y: view.y + sy / view.scale
});

export const toScreen = (view: View, wx: number, wy: number): { x: number; y: number } => ({
  x: (wx - view.x) * view.scale,
  y: (wy - view.y) * view.scale
});

export const clampScale = (scale: number): number =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

/** Zoom about a screen point, so whatever is under the pointer stays there. */
export function zoomAround(view: View, sx: number, sy: number, factor: number): View {
  const scale = clampScale(view.scale * factor);
  const before = toWorld(view, sx, sy);
  const after = { x: before.x - sx / scale, y: before.y - sy / scale };
  return { x: after.x, y: after.y, scale };
}

/** A rectangle with its width and height positive, whichever corner it was
 *  dragged from. */
export function normalise(rect: Rect): Rect {
  return {
    x: rect.w < 0 ? rect.x + rect.w : rect.x,
    y: rect.h < 0 ? rect.y + rect.h : rect.y,
    w: Math.abs(rect.w),
    h: Math.abs(rect.h)
  };
}

/** The box an element occupies in world units. A stroke's is its points; every
 *  other type's is its own rectangle, cornered whichever way it was drawn. */
export function elementBounds(el: BoardElement): Rect {
  if (el.type === 'stroke' && el.points && el.points.length >= 2) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i + 1 < el.points.length; i += 2) {
      const px = el.x + el.points[i]!;
      const py = el.y + el.points[i + 1]!;
      minX = Math.min(minX, px);
      minY = Math.min(minY, py);
      maxX = Math.max(maxX, px);
      maxY = Math.max(maxY, py);
    }
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }
  return normalise({ x: el.x, y: el.y, w: el.w, h: el.h });
}

/** The box around everything, or null for an empty board. */
export function unionBounds(elements: BoardElement[]): Rect | null {
  if (elements.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const el of elements) {
    const b = elementBounds(el);
    minX = Math.min(minX, b.x);
    minY = Math.min(minY, b.y);
    maxX = Math.max(maxX, b.x + b.w);
    maxY = Math.max(maxY, b.y + b.h);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Everything in view, with a margin. An empty board goes back to 1:1 at the
 *  origin rather than dividing by a zero-sized box. */
export function fitView(
  elements: BoardElement[],
  viewportW: number,
  viewportH: number,
  pad = 48
): View {
  const box = unionBounds(elements);
  if (!box || viewportW <= 0 || viewportH <= 0) return { x: 0, y: 0, scale: 1 };

  const w = Math.max(box.w, 1);
  const h = Math.max(box.h, 1);
  const scale = clampScale(Math.min((viewportW - pad * 2) / w, (viewportH - pad * 2) / h));
  return {
    x: box.x + box.w / 2 - viewportW / 2 / scale,
    y: box.y + box.h / 2 - viewportH / 2 / scale,
    scale
  };
}

/** Distance from a point to a segment, which is what every hit test is made
 *  of — a stroke is segments, a rectangle is four, an arrow is one. */
export function distanceToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(px - x1, py - y1);
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lengthSquared));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

const nearPolyline = (points: number[], px: number, py: number, tol: number): boolean => {
  for (let i = 0; i + 3 < points.length; i += 2) {
    const d = distanceToSegment(px, py, points[i]!, points[i + 1]!, points[i + 2]!, points[i + 3]!);
    if (d <= tol) return true;
  }
  // A one-point stroke is a dot, and a dot is still something to select.
  if (points.length === 2) return Math.hypot(px - points[0]!, py - points[1]!) <= tol;
  return false;
};

/**
 * Is this world point on this element?
 *
 * An outline is hit near its line, not inside it: an empty rectangle drawn
 * around other work is a frame, and a click in the middle of it belongs to
 * whatever is in there. A filled rectangle or a text box is hit anywhere in
 * its box, because there is something under the pointer.
 */
export function hitTest(el: BoardElement, px: number, py: number, tol = 6): boolean {
  if (el.type === 'stroke') {
    const pts = el.points ?? [];
    const moved: number[] = [];
    for (let i = 0; i + 1 < pts.length; i += 2) moved.push(el.x + pts[i]!, el.y + pts[i + 1]!);
    return nearPolyline(moved, px, py, tol + el.width / 2);
  }

  if (el.type === 'line' || el.type === 'arrow') {
    return (
      distanceToSegment(px, py, el.x, el.y, el.x + el.w, el.y + el.h) <= tol + el.width / 2
    );
  }

  const b = elementBounds(el);
  const inside = px >= b.x && px <= b.x + b.w && py >= b.y && py <= b.y + b.h;

  if (el.type === 'text') return inside;

  if (el.type === 'ellipse') {
    const rx = Math.max(b.w / 2, 1);
    const ry = Math.max(b.h / 2, 1);
    const nx = (px - (b.x + rx)) / rx;
    const ny = (py - (b.y + ry)) / ry;
    const d = Math.hypot(nx, ny);
    if (el.fill) return d <= 1;
    const band = tol / Math.min(rx, ry) + el.width / 2 / Math.min(rx, ry);
    return Math.abs(d - 1) <= band;
  }

  if (el.fill) return inside;
  const outline = [b.x, b.y, b.x + b.w, b.y, b.x + b.w, b.y + b.h, b.x, b.y + b.h, b.x, b.y];
  return nearPolyline(outline, px, py, tol + el.width / 2);
}

/** The topmost element under the point — last drawn, first picked. */
export function pickElement(
  elements: BoardElement[],
  px: number,
  py: number,
  tol = 6
): BoardElement | null {
  for (let i = elements.length - 1; i >= 0; i--) {
    const el = elements[i]!;
    if (hitTest(el, px, py, tol)) return el;
  }
  return null;
}

/**
 * Ramer–Douglas–Peucker, on the flat `[x0,y0,x1,y1,…]` a stroke stores.
 *
 * A pointer at 240 Hz makes a thousand points out of a line somebody drew in
 * four seconds, and every one of them is sent, stored and redrawn. Dropping the
 * ones that sit on a line between their neighbours changes nothing anybody can
 * see and is the difference between a board that stays under the frame limit
 * and one that does not.
 */
export function simplify(points: number[], tolerance = 1): number[] {
  const count = Math.floor(points.length / 2);
  if (count < 3 || tolerance <= 0) return points.slice(0, count * 2);

  const keep = new Uint8Array(count);
  keep[0] = 1;
  keep[count - 1] = 1;

  // Explicit stack rather than recursion: a long stroke is thousands of points
  // and the recursive form is a stack overflow on the worst input.
  const stack: [number, number][] = [[0, count - 1]];
  while (stack.length > 0) {
    const [first, last] = stack.pop()!;
    if (last <= first + 1) continue;

    const x1 = points[first * 2]!;
    const y1 = points[first * 2 + 1]!;
    const x2 = points[last * 2]!;
    const y2 = points[last * 2 + 1]!;

    let worst = -1;
    let worstAt = first;
    for (let i = first + 1; i < last; i++) {
      const d = distanceToSegment(points[i * 2]!, points[i * 2 + 1]!, x1, y1, x2, y2);
      if (d > worst) {
        worst = d;
        worstAt = i;
      }
    }

    if (worst > tolerance) {
      keep[worstAt] = 1;
      stack.push([first, worstAt], [worstAt, last]);
    }
  }

  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    if (keep[i]) out.push(points[i * 2]!, points[i * 2 + 1]!);
  }
  return out;
}

/**
 * The quadratic segments a smoothed stroke is drawn as: through every midpoint,
 * with the point itself as the control. The corners a simplified stroke leaves
 * behind become curves, and no point moves more than half a segment.
 */
export interface Curve {
  cx: number;
  cy: number;
  x: number;
  y: number;
}

export function smoothCurves(points: number[]): { start: [number, number]; curves: Curve[] } | null {
  const count = Math.floor(points.length / 2);
  if (count === 0) return null;

  const start: [number, number] = [points[0]!, points[1]!];
  const curves: Curve[] = [];
  for (let i = 1; i < count; i++) {
    const x = points[i * 2]!;
    const y = points[i * 2 + 1]!;
    const prevX = points[(i - 1) * 2]!;
    const prevY = points[(i - 1) * 2 + 1]!;
    curves.push(
      i === count - 1
        ? { cx: prevX, cy: prevY, x, y }
        : { cx: prevX, cy: prevY, x: (prevX + x) / 2, y: (prevY + y) / 2 }
    );
  }
  return { start, curves };
}
